// PDFViewerModal.jsx
import React, { useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Button, Spinner } from "components/ui";
import { HiOutlineDownload, HiOutlineX } from "react-icons/hi";
import { useIsMobile } from "utils/hooks/useIsMobile";

// Setup PDF.js worker (keep your existing approach)
pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

const OnlyPDFViewer = ({ fileUrl, onClose, message, onPdfError }) => {
  const [numPages, setNumPages] = useState(null);
  const [, setLoading] = useState(true);
  const [scale, setScale] = useState(1);
  const [lastShareMethod] = useState(null);

  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) {
      const baseScale = window.innerWidth / 800;
      setScale(baseScale < 1 ? baseScale : 1);
    } else {
      setScale(1);
    }
  }, [isMobile]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));
  const handleResetZoom = () => {
    if (isMobile) {
      const baseScale = window.innerWidth / 800;
      setScale(baseScale < 1 ? baseScale : 1);
    } else {
      setScale(1);
    }
  };

  const handleDownload = () => {
    if (!fileUrl) return;

    const link = document.createElement("a");
    let tempUrl = null;

    if (typeof fileUrl === "string") {
      link.href = fileUrl;
    } else {
      tempUrl = URL.createObjectURL(fileUrl);
      link.href = tempUrl;
    }

    link.setAttribute("download", "document.pdf");
    link.setAttribute("target", "_blank");

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    if (tempUrl) {
      setTimeout(() => URL.revokeObjectURL(tempUrl), 1000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div
        className={`relative bg-white rounded-lg shadow-xl flex flex-col
          ${
            isMobile
              ? "w-full h-full rounded-none"
              : "w-11/12 max-w-4xl max-h-[550px]"
          }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">Document Viewer</h3>
          <div className="flex items-center space-x-2">
            {!isMobile && (
              <Button
                variant="twoTone"
                size="sm"
                color="blue"
                onClick={handleDownload}
                icon={<HiOutlineDownload />}
              >
                Download
              </Button>
            )}

            <Button
              variant="plain"
              size="sm"
              icon={<HiOutlineX />}
              onClick={onClose}
            />
          </div>
        </div>

        {/* Footer (moved to top, under header) */}
        {numPages && (
          <div className="border-b p-4 flex justify-end">
            <div className="flex items-center space-x-2">
              <Button variant="plain" size="sm" onClick={handleZoomOut}>
                -
              </Button>

              <span>{Math.round(scale * 100)}%</span>

              <Button variant="plain" size="sm" onClick={handleZoomIn}>
                +
              </Button>

              <Button variant="plain" size="sm" onClick={handleResetZoom}>
                Reset
              </Button>
            </div>
          </div>
        )}

        {/* PDF Viewer */}
        <div className="flex-grow overflow-auto p-2">
          {message ? (
            <div className="text-center text-red-600 text-lg">{message}</div>
          ) : (
            <div className="flex justify-center min-w-max">
              <Document
                file={fileUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<Spinner size={40} />}
                onLoadError={(error) => {
                  console.error("Error loading PDF:", error);

                  if (onPdfError) {
                    onPdfError(error);
                    return;
                  }
                }}
              >
                {Array.from(new Array(numPages || 0), (_, index) => (
                  <div key={index} className="flex justify-center mb-6">
                    <Page
                      pageNumber={index + 1}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      scale={scale}
                    />
                  </div>
                ))}
              </Document>
            </div>
          )}
        </div>

        {/* optional small feedback area about share status */}
        <div className="p-2 text-right">
          {lastShareMethod && (
            <small className="text-gray-500">
              Last share method: {lastShareMethod}
            </small>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnlyPDFViewer;
